import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Chip, CircularProgress, Divider, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function Amount({ n, set }) {
  if (n === null || n === undefined) return <Chip size="small" label="Unlimited" color="success" />;
  // A set is the new total, not sessions added on top.
  if (set) return <Typography component="span" fontWeight={700}>Set to {n}</Typography>;
  const back = n < 0;
  return (
    <Typography component="span" fontWeight={700} color={back ? 'error.main' : 'success.main'}>
      {back ? `− ${Math.abs(n)}` : `+ ${n}`}
    </Typography>
  );
}

/**
 * What this level has handed down, and what it was given.
 *
 * Both are already recorded — a hand-down and a take-back are one ledger each way — but
 * until now they could only be read one account at a time. An administrator wants the
 * other question: what has this level actually given out, and to whom.
 *
 * `endpoint` is the level's own history route, so the same table serves a region, an
 * organisation and an enterprise.
 */
export default function CreditHistory({ endpoint }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (search) => {
    try {
      const { data: d } = await axios.get(
        `${API_URL}${endpoint}${search ? `?q=${encodeURIComponent(search)}` : ''}`);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);   // a keystroke is not a request
    return () => clearTimeout(t);
  }, [q, load]);

  if (loading) {
    return <Card sx={{ mt: 3 }}><CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={26} />
    </CardContent></Card>;
  }
  if (!data) return null;

  const given = data.given || { movements: [] };
  const received = data.received || { movements: [] };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" fontWeight={700}>Session history</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every movement of AI sessions in and out of this {data.node?.label || 'level'} —
          what was handed down, what was taken back, and what you were given in the first
          place.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip size="small" color="success" variant="outlined"
            label={`${given.total_given || 0} handed down`} />
          <Chip size="small" color="error" variant="outlined"
            label={`${given.total_taken_back || 0} taken back`} />
        </Stack>

        <TextField
          size="small" fullWidth value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by who received them…"
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ mb: 1 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Handed down ({given.count || 0})
        </Typography>
        {given.movements.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Nothing handed down yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who received them</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {given.movements.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(m.at)}</TableCell>
                    <TableCell>
                      {m.to_name}{' '}
                      <Chip size="small" variant="outlined" label={m.to_label} sx={{ ml: 0.5 }} />
                    </TableCell>
                    <TableCell align="right"><Amount n={m.amount_sessions} set={m.direction === 'set'} /></TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{m.by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {received.movements.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              What you were given ({received.count || 0})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {received.movements.map((m) => (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(m.at)}</TableCell>
                      <TableCell>{m.from_platform ? 'ScribeLive' : m.from_name}</TableCell>
                      <TableCell align="right"><Amount n={m.amount_sessions} set={m.direction === 'set'} /></TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{m.by || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
